// src-lit/components/product/kd-product-pricing.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { onEvent } from '../../utils/dom';
import { EVENT_NAMES, type VariantSelectedDetail } from '../../constants/events';
import { SELECTORS } from '../../constants/selectors';
import { safeJsonParse } from '../../utils/data';
import { calculateDiscountRate, isOnSale } from './price.logic';

interface UnitPriceEntry {
  unitPrice: number;
  unitName: string;
}
type UnitPriceMap = Record<string, UnitPriceEntry | null>;

interface ShippingPriceEntry {
  /**
   * 会員価格スロット（税込・送料込、円×100）。
   * 通常は一般会員価格。寄付会員ログイン時は donation 価格を入れる
   * （snippets/v2-product-pricing.liquid のマップ生成で SSR 時に切替）。
   */
  general: number;
  /**
   * プレミアム会員価格（税込・送料込、円×100）。
   * _premium 派生 metafield 未設定の variant、および寄付会員ログイン時は null。
   * null のときプレミアム会員には一般価格を表示し、navy 特典パネルを hidden にする。
   */
  premium: number | null;
}
type ShippingPriceMap = Record<string, ShippingPriceEntry>;

/**
 * 会員種別。閲覧者で一定（ログイン状態の変化はページ再読込を伴う）ため SSR が
 * data-member-tier で確定させる。variant 単位の premium 価格有無は別途 map で判定する。
 */
type MemberTier = 'general' | 'premium' | 'donation';

/**
 * @element kd-product-pricing
 * @description バリアント選択に応じて価格表示を動的更新する。
 *   kd-product-variant-selected イベントを購読し、会員種別ごとの主価格・比較価格・
 *   割引率・単価に加え、緑ナッジ（一般/未ログイン）と navy 特典パネル（プレミアム）を更新する。
 */
@customElement('kd-product-pricing')
export class KdProductPricing extends LitElement {
  createRenderRoot() {
    return this;
  }

  private unsubscribe: (() => void) | null = null;

  private unitPriceMap: UnitPriceMap = {};

  private shippingPriceMap: ShippingPriceMap = {};

  private tier: MemberTier = 'general';

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.tier = (this.dataset.memberTier as MemberTier | undefined) ?? 'general';
    this.unitPriceMap = this.parseMap<UnitPriceMap>(SELECTORS.PRODUCT_PRICING.UNIT_PRICE_MAP);
    this.shippingPriceMap = this.parseMap<ShippingPriceMap>(
      SELECTORS.PRODUCT_PRICING.SHIPPING_PRICE_MAP,
    );
    this.unsubscribe = onEvent<VariantSelectedDetail>(
      document,
      EVENT_NAMES.PRODUCT.VARIANT_SELECTED,
      (e) => this.handleVariantSelected(e.detail),
    );
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  /** script[type=application/json] のマップを読み込む。失敗時は空マップ。 */
  private parseMap<T extends object>(selector: string): T {
    const script = this.querySelector<HTMLScriptElement>(selector);
    return safeJsonParse<T>(script?.textContent, {} as T);
  }

  private handleVariantSelected(detail: VariantSelectedDetail): void {
    this.updatePrice(detail.price);
    const showPremiumPrice = this.updateMemberPricing(detail.variantId, detail.price);
    this.updateSaleState(detail.price, detail.compareAtPrice, showPremiumPrice);
    this.updateUnitPrice(detail.variantId);
  }

  /** 定期便レイアウトの単一価格を更新する */
  private updatePrice(price: number): void {
    const el = this.querySelector<HTMLElement>(SELECTORS.PRODUCT_PRICING.PRICE);
    if (el) {
      el.textContent = this.formatPrice(price);
    }
  }

  /**
   * 会員種別ごとの主価格と、緑ナッジ / navy 特典パネルの表示状態を更新する。
   * 一般/未ログイン・寄付: 主価格 = general（マップ未設定なら variant.price にフォールバック）。
   * プレミアム: 主価格 = premium。ただし当該 variant に premium が無い（null）ときは
   *   一般価格へ落とし、ラベルと会員価格色（#e6370a）も一般表示へ戻す。
   * @returns showPremiumPrice（この variant でプレミアム主価格を表示するか）。sale group の
   *   レイアウト切替（updateSaleState）と揃えるため呼び出し側へ返す。
   */
  private updateMemberPricing(variantId: string, fallbackPrice: number): boolean {
    const entry = this.shippingPriceMap[variantId];
    const general = entry?.general ?? fallbackPrice;
    const premium = entry?.premium ?? null;

    const showPremiumPrice = this.tier === 'premium' && premium !== null;
    // premium !== null を条件に含めて narrowing を効かせる（showPremiumPrice からは導出されない）
    const mainPrice = this.tier === 'premium' && premium !== null ? premium : general;

    const mainEl = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_PRICING.MAIN_PRICE,
    );
    if (mainEl) {
      mainEl.textContent = this.formatPrice(mainPrice);
    }

    if (this.tier === 'premium') {
      const labelEl = this.querySelector<HTMLElement>(
        SELECTORS.PRODUCT_PRICING.MAIN_LABEL,
      );
      if (labelEl) {
        labelEl.textContent = showPremiumPrice ? 'プレミアム会員価格' : '一般会員価格';
      }

      const wrapEl = this.querySelector<HTMLElement>(
        SELECTORS.PRODUCT_PRICING.MAIN_PRICE_WRAP,
      );
      if (wrapEl) {
        wrapEl.classList.toggle('tw:text-primary2', showPremiumPrice);
        wrapEl.classList.toggle('tw:text-text-main', !showPremiumPrice);
      }
    }

    this.updateNudge(premium);
    this.updateBenefit(premium);

    return showPremiumPrice;
  }

  /** 緑ナッジ（未ログイン/一般会員）。premium 価格が無い variant では hidden にする。 */
  private updateNudge(premium: number | null): void {
    const nudge = this.querySelector<HTMLElement>(SELECTORS.PRODUCT_PRICING.NUDGE);
    if (!nudge) return;

    if (premium === null) {
      nudge.hidden = true;
      return;
    }
    nudge.hidden = false;

    const premiumEl = nudge.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_PRICING.NUDGE_PREMIUM,
    );
    if (premiumEl) {
      premiumEl.textContent = this.formatPrice(premium);
    }
    // 課題管理表 #90: おトク額（= general - premium ＝ base 送料差分）の
    // 「（¥Yおトク）」表記を撤去したため、ナッジ内で更新する金額は premium のみ。
  }

  /**
   * navy 特典パネル（プレミアム会員）。premium 価格が無い variant では hidden にする。
   * 課題管理表 #90 でお得額 / 一般価格の金額表示を撤去したため、更新するのは表示可否のみ。
   */
  private updateBenefit(premium: number | null): void {
    const benefit = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_PRICING.BENEFIT,
    );
    if (!benefit) return;

    if (premium === null) {
      benefit.hidden = true;
      return;
    }
    benefit.hidden = false;
  }

  /**
   * セール表示（参考小売価格・割引率）を更新する。
   * プレミアム tier では variant により premium↔一般 が切り替わり、参考小売/OFF の並び・装飾も
   * 変わるため、SSR が両レイアウトを描いておき per-variant の showPremiumPrice でどちらを表示するか
   * 切替える。両レイアウトに compare-price / discount-rate が存在するため querySelectorAll で
   * 全コピーを更新する（隠れている側も切替時に正しい値になるように）。
   */
  private updateSaleState(
    price: number,
    compareAtPrice: number | null,
    showPremiumPrice: boolean,
  ): void {
    const saleGroup = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_PRICING.SALE_GROUP,
    );
    if (!saleGroup) return;

    if (!(compareAtPrice && isOnSale(price, compareAtPrice))) {
      saleGroup.hidden = true;
      return;
    }
    saleGroup.hidden = false;

    // プレミアム tier のみ両レイアウトが存在。showPremiumPrice で表示を切替える
    // （一般/寄付 tier は general レイアウト単一で、この要素は存在しないため no-op）。
    const premiumLayout = saleGroup.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_PRICING.SALE_LAYOUT_PREMIUM,
    );
    const generalLayout = saleGroup.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_PRICING.SALE_LAYOUT_GENERAL,
    );
    if (premiumLayout && generalLayout) {
      premiumLayout.hidden = !showPremiumPrice;
      generalLayout.hidden = showPremiumPrice;
    }

    const comparePriceText = this.formatPrice(compareAtPrice);
    saleGroup
      .querySelectorAll<HTMLElement>(SELECTORS.PRODUCT_PRICING.COMPARE_PRICE)
      .forEach((el) => {
        el.textContent = comparePriceText;
      });

    const discountRateText = String(calculateDiscountRate(price, compareAtPrice));
    saleGroup
      .querySelectorAll<HTMLElement>(SELECTORS.PRODUCT_PRICING.DISCOUNT_RATE)
      .forEach((el) => {
        el.textContent = discountRateText;
      });
  }

  private updateUnitPrice(variantId: string): void {
    const unitGroup = this.querySelector<HTMLElement>(
      SELECTORS.PRODUCT_PRICING.UNIT_GROUP,
    );
    if (!unitGroup) return;

    const entry = this.unitPriceMap[variantId];
    if (entry) {
      unitGroup.hidden = false;

      const labelEl = this.querySelector<HTMLElement>(
        SELECTORS.PRODUCT_PRICING.UNIT_LABEL,
      );
      if (labelEl) {
        labelEl.textContent = `1${entry.unitName}あたり`;
      }

      const priceEl = this.querySelector<HTMLElement>(
        SELECTORS.PRODUCT_PRICING.UNIT_PRICE,
      );
      if (priceEl) {
        priceEl.textContent = this.formatPrice(entry.unitPrice);
      }
    } else {
      unitGroup.hidden = true;
    }
  }

  private formatPrice(price: number): string {
    return Math.floor(price / 100).toLocaleString('ja-JP');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-product-pricing': KdProductPricing;
  }
}
